import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Package, ShoppingBag, Archive, Truck, Zap, Lock, Users, Settings, LogOut, Mail, Inbox,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { setSEO } from "@/lib/api";

const SECTIONS = [
  { to: "/admin", label: "Overview", icon: Zap, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/drop-manager", label: "Drop Manager", icon: Zap },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/inventory", label: "Inventory", icon: Archive },
  { to: "/admin/suppliers", label: "Suppliers", icon: Truck },
  { to: "/admin/contraband-access", label: "Contraband Access", icon: Lock },
  { to: "/admin/drop-list", label: "Drop List", icon: Mail },
  { to: "/admin/notify", label: "Notify Requests", icon: Inbox },
  { to: "/admin/contacts", label: "Contacts", icon: Inbox },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setSEO({ title: "Admin Console | FEMBOT NEX", description: "Restricted." });
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-32 font-mono text-xs tracking-[0.2em] uppercase text-[#7a6a90]">
        // verifying clearance...
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  function doLogout() {
    logout();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div data-testid="admin-shell" className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">// CONTROL PANEL</div>
          <h1 className="mt-2 font-display text-3xl md:text-5xl tracking-tight">
            Admin <span className="text-[#ff1493]">Console</span>
          </h1>
          <div className="mt-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#7a6a90]">
            Logged in as {user.email} · Role: {user.role}
          </div>
        </div>
        <button data-testid="admin-logout" onClick={doLogout} className="btn-outline">
          <LogOut size={14} strokeWidth={1.5} /> Logout
        </button>
      </div>

      <div className="grid md:grid-cols-12 gap-px bg-[#2a1a3a] border border-[#2a1a3a]">
        <aside className="md:col-span-3 bg-[#06000e] p-4">
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible" data-testid="admin-sidebar">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const key = s.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <NavLink
                  key={s.to}
                  to={s.to}
                  end={s.end}
                  data-testid={`admin-nav-${key}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-left font-mono text-[11px] tracking-[0.18em] uppercase whitespace-nowrap border-l-2 transition-colors ${
                      isActive
                        ? "border-[#ff1493] bg-[#14091f] text-white"
                        : "border-transparent text-[#b4a3c7] hover:bg-[#0a0316] hover:text-white"
                    }`
                  }
                >
                  <Icon size={14} strokeWidth={1.5} />
                  {s.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main className="md:col-span-9 bg-[#050010] p-6 md:p-10 min-h-[600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
